<script>
	import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
	import { toast } from '@zerodevx/svelte-toast';
	import { projectState } from "@app/store/projectStore.svelte.js";
	import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
	import { speakerState } from "@app/store/speakerStore.svelte.js";
	import { markerState } from "@app/store/markerStore.svelte.js";
	import { issueState } from "@app/store/issueStore.svelte.js";
	import { metadataState } from "@app/store/metadataStore.svelte.js";
	import { styleState } from "@app/store/styleStore.svelte.js";
	import { establishHistoryBaseline } from "@app/store/historyStore.svelte.js";
	import { authState } from "@app/store/authStore.svelte.js";
	import { fade } from "svelte/transition";
	import { onDestroy } from "svelte";
	import * as m from "@app/paraglide/messages.js";
	/* Firebase */
	import { auth, functions } from "@app/configs/firebase.js";
	import { httpsCallable } from "firebase/functions";
	import storage from "@app/configs/storage.js";
	import { ref, uploadString } from "firebase/storage";
	import db from "@app/configs/firestore.js";
	import { collection, doc, onSnapshot } from "firebase/firestore";

	import { ccprjFunc } from '@app/lib/ccprj.js';
	import { applyTeamCommit } from "@app/lib/applyTeamCommit.js";

    import Swal from 'sweetalert2'
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

	let userId = auth.currentUser?.uid;
	let progress = $state(0),
        processing = $state(false),
        commitMessage = $state(""),
		statusMsg = $state(m.team_sync_status_retrieving_latest_commit()),
		unsubscribe,
		registeredSnapshotId = $state("");

	function escapeHtml(value) {
		return String(value ?? "")
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");
	}

	function closeIfIdle() {
		if (processing) {
			return;
		}
		hideModal();
	}

	async function failRegisteredJob() {
		if (!registeredSnapshotId || !authState.team?.id || !projectState.teamId) {
			return;
		}
		try {
			await httpsCallable(functions, "v8FailProjectSync")({
				id: registeredSnapshotId,
				projectTeamId: projectState.teamId,
			});
		} catch (err) {
			console.error("Unable to fail sync job", err);
		}
	}

	onDestroy(() => {
		if (unsubscribe) {
			unsubscribe();
		}
	});

	async function retrieveSnapshot(commitFileUrl) {
		try {
			console.log("Retrieving Commit File");
			progress = 45;
			statusMsg = m.team_sync_status_retrieving_latest_commit();
			let commitFileRes = await fetch(commitFileUrl);
			if (!commitFileRes.ok) {
				throw new Error(`Failed to download commit (${commitFileRes.status})`);
			}
			let commitFileJson = await commitFileRes.json();
			console.log("COMMIT FILE JSON:", commitFileJson);

			progress = 45;
			statusMsg = m.team_sync_status_loading_commit();

			const { eventGroupsNotInShare } = await applyTeamCommit(commitFileJson);

            if (eventGroupsNotInShare.length > 0){
                Swal.fire({
					...SWAL_BASE,
					customClass: SWAL_CLASSES.ok,
					title: m.team_sync_events_not_in_team_project_title(),
					html: `${m.team_sync_events_not_in_team_project_intro()}<br><br> <ul class="list-unstyled">${eventGroupsNotInShare.map((eventGroupName) => {
						return `<li>${escapeHtml(eventGroupName)}</li>`;
                    }).join("")}</ul>`,
                    icon: 'warning',
					confirmButtonText: m.team_sync_events_not_in_team_project_confirm(),
                    allowOutsideClick : false,
                    allowEscapeKey : false,
                });
            }

            
			progress = 100;
			statusMsg = m.team_sync_status_done();

			establishHistoryBaseline({ name: "project sync" });

			toast.push(m.team_sync_completed_successfully(), {classes: ['toast-success']});
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});
		} finally {
			processing = false;
			hideModal();
		}
	}

	async function syncProject() {
		toast.push(m.team_sync_starting_info(), {classes: ['toast-info']});

        processing = true;
		registeredSnapshotId = "";

		try {
			if (!authState.team || !authState.team.id) {
				throw new Error(m.team_sync_missing_team_error());
			}

			let commitFileUrl;
			let teamId = authState.team.id;
			let storageRef = ref(storage);
			let snapshotId = `${Date.now().toString()}_${userId}`;
			let fileName = `${snapshotId}.json`;
			let dbRef = collection(db, "teams", teamId, "projects", projectState.teamId, "snapshots");
			let uploadRef = ref(storageRef, `teams/${teamId}/projects/${projectState.teamId}/snapshots/${fileName}`);

			let projectJson = ccprjFunc.createCcprj({
                project : JSON.parse(JSON.stringify(projectState)),
                metadata : JSON.parse(JSON.stringify(metadataState)),
                speakers : JSON.parse(JSON.stringify(speakerState)),
                issues : JSON.parse(JSON.stringify(issueState.value)),
                markers : JSON.parse(JSON.stringify(markerState)),
                eventGroups : JSON.parse(JSON.stringify(eventGroupState)),
                style : JSON.parse(JSON.stringify(styleState))
            });

			try {
				progress = 5;
				let registerProjectRes = await httpsCallable(functions, "v8RegisterProjectSync")({
					id: snapshotId,
					projectTeamId: projectState.teamId,
                    message : commitMessage,
					name: projectState.name
				});

				if (registerProjectRes && registerProjectRes.data) {
					console.log(JSON.stringify(registerProjectRes));
					throw new Error(registerProjectRes.data);
				}
				registeredSnapshotId = snapshotId;
			} catch (err) {
				console.error("Unable to register snapshot");
				console.log(err, err.message);
				toast.push(m.team_sync_register_job_error({ message: err.message }), {classes: ['toast-danger']});

				processing = false;
				hideModal();
				return;
			}

			progress = 15;
			statusMsg = m.team_sync_status_uploading_snapshot();
			try {
				await uploadString(uploadRef, JSON.stringify(projectJson));
			} catch (uploadErr) {
				await failRegisteredJob();
				throw uploadErr;
			}
			progress = 25;

			statusMsg = m.team_sync_status_syncing_and_generating_commit();
			try {
				commitFileUrl = await httpsCallable(functions, "v8SyncProjectV3", { timeout: 540000 })({
					id: snapshotId,
					projectTeamId: projectState.teamId,
				});

				await retrieveSnapshot(commitFileUrl.data[0]);
			} catch (err) {
				console.log(err, err.message);
				progress = 35;
				unsubscribe = onSnapshot(doc(dbRef, snapshotId), (docSnap) => {
					console.log(docSnap.data());
					const stop = () => {
						if (unsubscribe) {
							unsubscribe();
							unsubscribe = undefined;
						}
					};
					if (docSnap.data() === undefined) {
						console.log(err, err.message);
						toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});
						processing = false;
						hideModal();
						stop();
						return;
					}

					let commitData = docSnap.data();
					if (commitData.status === "in progress") {
						return;
					} else if (commitData.status === "complete") {
						stop();
						retrieveSnapshot(commitData.signedFileUrl[0]);
					} else if (commitData.status === "failed") {
						toast.push(m.team_sync_commit_status_failed({ message: err.message }), {classes: ['toast-danger']});
						processing = false;
						hideModal();
						stop();
					} else {
						toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});
						processing = false;
						hideModal();
						stop();
					}
				});
			}
		} catch (err) {
			console.log(err, err.message);
			await failRegisteredJob();
			toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});

			processing = false;
			hideModal();
		}
	}
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'teamSync' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="teamSyncTitle"
    aria-modal="true"
    tabindex="-1" 
    id="TeamSync">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
				<h4 class="modal-title" id="teamSyncTitle">{m.team_sync_title()}</h4>
				<button type="button" class="btn-close" aria-label={m.close()} onclick={closeIfIdle} disabled={processing}></button>
            </div>
            <div class="modal-body">
                {#if processing}
				<p><i class="bi bi-exclamation-diamond-fill" aria-hidden="true"></i> {m.team_sync_leave_window_open_notice()}</p>
                <div class="progress" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" style="width: {progress}%;">{progress}%</div>
                </div>
                <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
                {:else}
                <form>
                    <div class="mb-3">
						<label for="commitMessageArea" class="form-label">{m.team_sync_commit_message_label()} <small>({m.team_sync_optional_label()})</small></label>
                        <textarea 
                            bind:value={commitMessage} 
                            class="form-control" 
                            id="commitMessageArea" 
                            rows="3" 
							placeholder={m.team_sync_commit_message_placeholder()}
							aria-describedby="commitMessageHelp"></textarea>
						<div id="commitMessageHelp" class="form-text">{m.team_sync_commit_message_help()}</div>
                    </div>					
					<button type="button" class="btn btn-light float-end" id="syncCancelBtn" aria-label={m.team_sync_cancel_label()} onclick={closeIfIdle}>{m.team_sync_cancel_button()}</button>
					<button type="button" class="btn btn-primary float-end mx-2" id="syncChangesBtn" aria-label={m.team_sync_start_label()} onclick={syncProject}>{m.team_sync_sync_changes_button()}</button>
                </form>
                {/if}			
            </div>
        </div>
    </div>
</div>
